import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/components/status";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Wallet, TrendingUp, Briefcase } from "lucide-react";

const AXIS_TICK = { fontSize: 12, fill: "#64748B" };
const BAR_RADIUS = [4, 4, 0, 0];

export default function Earnings() {
  const [earnings, setEarnings] = useState({});
  const [payouts, setPayouts] = useState([]);

  useEffect(() => {
    api.get("/earnings").then((r) => setEarnings(r.data));
    api.get("/payouts").then((r) => setPayouts(r.data));
  }, []);

  const paid = payouts.filter((p) => p.status === "paid");
  const chart = (() => {
    const map = {};
    paid.forEach((p) => {
      const m = new Date(p.created_at).toLocaleDateString([], { month: "short", day: "numeric" });
      map[m] = (map[m] || 0) + (p.net_to_cleaner ?? p.amount);
    });
    return Object.entries(map).slice(-8).map(([name, amount]) => ({ name, amount }));
  })();

  return (
    <div className="space-y-6" data-testid="earnings-page">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Earnings</h1>
        <p className="text-muted-foreground text-sm mt-1">Track your income and payout history.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-border rounded-md bg-sapphire text-white p-5" data-testid="earn-total">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.2em] text-white/60">Total Earned</span><Wallet className="w-4 h-4" /></div>
          <div className="font-heading font-black text-4xl mt-2">{money(earnings.total_earned)}</div>
        </div>
        <div className="border border-border rounded-md bg-white p-5">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Jobs Completed</span><Briefcase className="w-4 h-4 text-ocean" /></div>
          <div className="font-heading font-black text-4xl text-sapphire mt-2">{earnings.jobs_completed || 0}</div>
        </div>
        <div className="border border-border rounded-md bg-white p-5">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Platform Fees</span><TrendingUp className="w-4 h-4 text-ocean" /></div>
          <div className="font-heading font-black text-4xl text-sapphire mt-2">{money(earnings.platform_fees_paid)}</div>
        </div>
      </div>

      {chart.length > 0 && (
        <div className="border border-border rounded-md bg-white p-5">
          <h2 className="font-heading font-bold text-lg text-sapphire mb-4">Payout Trend</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip formatter={money} />
              <Bar dataKey="amount" fill="#0052CC" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div>
        <h2 className="font-heading font-bold text-xl text-sapphire mb-3">Payout History</h2>
        <div className="border border-border rounded-md bg-white divide-y divide-border">
          {payouts.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No payouts yet.</div>}
          {payouts.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-5 py-4" data-testid={`earn-payout-${p.id}`}>
              <div>
                <div className="font-semibold text-sapphire">Cleaning payout</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(p.created_at).toLocaleString()} · {money(p.base_amount ?? p.amount)} − {money(p.cleaner_fee || 0)} fee · {p.mode}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-emerald-600">+{money(p.net_to_cleaner ?? p.amount)}</div>
                <span className="text-xs text-muted-foreground">{p.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
